import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { QUERY_STATUS } from "constants/constants";
import { canUserPerformAction } from "access-roles/general-functions";
import { FAF_NEED_ROLES } from "access-roles/roles";
import { useUserContext } from "api/user/UserProvider";
import { toNeedPayload } from "utils/fafHelper";
import {
  createNeedQuery,
  deleteNeedQuery,
  getActiveNeedsQuery,
  getNeedReviewsQuery,
  getNeedsByDeclarationQuery,
  patchNeedReviewQuery,
  updateNeedQuery,
} from "./queries";

const NeedContext = createContext({});

export const useNeedContext = () => useContext(NeedContext);

// Two needs are "identical" if their back payloads are (avoids a useless PUT).
const isNeedChanged = (loaded, current, declarationId) =>
  JSON.stringify(toNeedPayload(loaded, declarationId)) !==
  JSON.stringify(toNeedPayload(current, declarationId));

// Staffing needs — mission service. `activeNeeds` (GET /need) feeds the
// "besoin concerné" select of the qualifs step; `needReviews` (GET /need-review/<decl>)
// is the to-review list of the needs step, each answer being saved on the fly (PATCH).
export const NeedProvider = ({ ...props }) => {
  const { userRoleList } = useUserContext();

  const [activeNeeds, setActiveNeeds] = useState([]);
  const [needReviews, setNeedReviews] = useState([]); // [{ need, still_active }]
  const [loadStatus, setLoadStatus] = useState(QUERY_STATUS.INITIAL);
  const activeLoadedRef = useRef(false);
  const reviewsDeclarationRef = useRef(null); // keyed on the already-loaded declaration

  // Active needs (select). Single load via a ref: the list can be empty and a
  // .length guard would re-fetch in a loop (setState([]) recreates the reference).
  const getActiveNeeds = useCallback(async () => {
    if (activeLoadedRef.current) return;
    if (
      !canUserPerformAction({
        userRoles: userRoleList,
        requiredRoles: FAF_NEED_ROLES.ROLE_GET_NEED,
      })
    )
      return;
    activeLoadedRef.current = true;
    setLoadStatus(QUERY_STATUS.LOADING);
    try {
      setActiveNeeds((await getActiveNeedsQuery()) || []);
      setLoadStatus(QUERY_STATUS.SUCCESS);
    } catch (e) {
      activeLoadedRef.current = false;
      console.error("getActiveNeeds", e);
      setLoadStatus(QUERY_STATUS.ERROR);
    }
  }, [userRoleList]);

  // Needs attached to a declaration (edit list "being added"):
  // plain read that returns the list (the wizard rebuilds it into forms). Network
  // errors are propagated (the caller distinguishes "empty" from "failure" to retry).
  const getNeedsByDeclaration = useCallback(
    async (declarationId) => {
      if (
        !declarationId ||
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_NEED_ROLES.ROLE_GET_NEED,
        })
      )
        return [];
      return (await getNeedsByDeclarationQuery(declarationId)) || [];
    },
    [userRoleList]
  );

  // Need reviews of a declaration, returned WITHOUT touching the state (no ref-guard):
  // used for step inference on resume (have we already ruled on any pending needs?).
  // Errors propagated (the caller distinguishes "empty" from "failure"). Missing role → [].
  const getNeedReviewsByDeclaration = useCallback(
    async (declarationId) => {
      if (
        !declarationId ||
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_NEED_ROLES.ROLE_GET_NEED,
        })
      )
        return [];
      return (await getNeedReviewsQuery(declarationId)) || [];
    },
    [userRoleList]
  );

  // List of needs to review for the current declaration (reloaded if it changes).
  const getNeedReviews = useCallback(
    async (declarationId) => {
      if (!declarationId || reviewsDeclarationRef.current === declarationId) return;
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_NEED_ROLES.ROLE_GET_NEED,
        })
      )
        return;
      reviewsDeclarationRef.current = declarationId;
      try {
        setNeedReviews((await getNeedReviewsQuery(declarationId)) || []);
      } catch (e) {
        reviewsDeclarationRef.current = null;
        console.error("getNeedReviews", e);
      }
    },
    [userRoleList]
  );

  // Immediate save (on click) of a review answer: { need_id, still_active } (PATCH upsert).
  const saveNeedReview = useCallback(
    async (declarationId, payload) => {
      if (!declarationId) return;
      // Mutation guard: fail loudly (see DeclarationProvider.createDeclaration). The caller
      // (setReview) rolls back the optimistic answer and surfaces the error on rejection.
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: FAF_NEED_ROLES.ROLE_UPDATE_NEED,
        })
      )
        throw new Error("faf_missing_role");
      await patchNeedReviewQuery({ declarationId, payload });
      // A review answer can change a need's activity (still_active) → invalidate the
      // active list so the qualifs "besoin concerné" select reflects it on next load.
      activeLoadedRef.current = false;
    },
    [userRoleList]
  );

  // Synchronizes the step's new needs (create/update/delete) against the last
  // synchronized snapshot, so that going back and forth on the step does not recreate a duplicate:
  //  - need without id → POST (we grab the returned id and reinject it);
  //  - need with id    → PUT (only if it changed);
  // - need present in the snapshot but removed from the list → DELETE.
  // Returns the reconciled list (ids filled in): the caller keeps it as the new
  // snapshot. Deletion is guarded by UPDATE_NEED (the DELETE route requires it on the back side).
  // Invalidates the active list so the "besoin concerné" select (qualifs) sees the created ones.
  const syncNeeds = useCallback(
    async ({ declarationId, needs, loaded }) => {
      const can = (role) =>
        canUserPerformAction({ userRoles: userRoleList, requiredRoles: role });
      // Mutation guard fails loudly (see DeclarationProvider.createDeclaration): throw before
      // touching the server if the diff needs a role the user lacks, rather than silently
      // no-oping (which would leave created needs id-less and the step falsely "done").
      const requireRole = (role) => {
        if (!can(role)) throw new Error("faf_missing_role");
      };
      const loadedList = loaded || [];
      const currentList = needs || [];
      const keptIds = currentList.filter((n) => n.id).map((n) => n.id);
      const loadedById = new Map(loadedList.map((l) => [l.id, l]));

      const toDelete = loadedList.filter((l) => l.id && !keptIds.includes(l.id));
      const toCreate = currentList.filter((n) => !n.id);
      const toUpdate = currentList.filter(
        (n) => n.id && isNeedChanged(loadedById.get(n.id) || {}, n, declarationId)
      );
      // DELETE + PUT routes both require UPDATE_NEED on the back side.
      if (toDelete.length || toUpdate.length) requireRole(FAF_NEED_ROLES.ROLE_UPDATE_NEED);
      if (toCreate.length) requireRole(FAF_NEED_ROLES.ROLE_CREATE_NEED);

      await Promise.all(toDelete.map((l) => deleteNeedQuery({ id: l.id })));

      const synced = await Promise.all(
        currentList.map(async (n) => {
          if (n.id) {
            if (isNeedChanged(loadedById.get(n.id) || {}, n, declarationId))
              await updateNeedQuery({
                id: n.id,
                payload: toNeedPayload(n, declarationId),
              });
            return n;
          }
          const created = await createNeedQuery({
            payload: toNeedPayload(n, declarationId),
          });
          return created?.id ? { ...n, id: created.id } : n;
        })
      );
      activeLoadedRef.current = false;
      return synced;
    },
    [userRoleList]
  );

  const values = useMemo(
    () => ({
      activeNeeds,
      needReviews,
      loadStatus,
      getActiveNeeds,
      getNeedsByDeclaration,
      getNeedReviews,
      getNeedReviewsByDeclaration,
      saveNeedReview,
      syncNeeds,
    }),
    [
      activeNeeds,
      needReviews,
      loadStatus,
      getActiveNeeds,
      getNeedsByDeclaration,
      getNeedReviews,
      getNeedReviewsByDeclaration,
      saveNeedReview,
      syncNeeds,
    ]
  );

  return <NeedContext.Provider value={values} {...props} />;
};
